import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, CreditCard } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", description: "", duration_min: 30, price: 0, category: "general", active: true, requires_payment: true, deposit_pct: 50, iva_included: true, overlap_enabled: false, compatible_service_ids: [] };

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  async function load() {
    const { data } = await api.get("/services");
    setItems(data);
  }
  useEffect(() => { load(); }, []);

  function openCreate() { setEditing(null); setForm(empty); setOpen(true); }
  function openEdit(s) { setEditing(s); setForm(s); setOpen(true); }

  async function save() {
    try {
      const payload = {
        ...form,
        duration_min: Number(form.duration_min),
        price: Number(form.price),
        deposit_pct: Number(form.deposit_pct ?? 100),
        requires_payment: !!form.requires_payment,
        iva_included: form.iva_included !== false,
        overlap_enabled: !!form.overlap_enabled,
        compatible_service_ids: form.overlap_enabled ? (form.compatible_service_ids || []) : [],
      };
      if (editing) await api.put(`/services/${editing.id}`, payload);
      else await api.post("/services", payload);
      toast.success("Guardado");
      setOpen(false);
      load();
    } catch { toast.error("Error al guardar"); }
  }

  async function remove(id) {
    if (!confirm("¿Eliminar servicio?")) return;
    await api.delete(`/services/${id}`);
    toast.success("Eliminado");
    load();
  }

  return (
    <div>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="overline">Catálogo</p>
          <h1 className="font-display text-4xl font-light tracking-tight mt-3">Servicios</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="services-new-btn"><Plus className="h-4 w-4 mr-2" /> Nuevo servicio</Button>
          </DialogTrigger>
          <DialogContent className="!grid-cols-none max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0">
            <DialogHeader className="shrink-0 p-6 pb-4"><DialogTitle>{editing ? "Editar servicio" : "Nuevo servicio"}</DialogTitle></DialogHeader>
            <div className="space-y-4 flex-1 min-h-0 overflow-y-auto px-6" data-testid="service-modal-body">
              <div className="space-y-2"><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-name-input" /></div>
              <div className="space-y-2"><Label>Descripción</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="service-desc-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Duración (min)</Label><Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} data-testid="service-duration-input" /></div>
                <div className="space-y-2"><Label>Precio</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="service-price-input" /></div>
              </div>
              <div className="space-y-2"><Label>Categoría</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="service-category-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="clinic">Clínica/Dental</SelectItem>
                    <SelectItem value="spa">Spa/Belleza</SelectItem>
                    <SelectItem value="consulting">Consultoría</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-[hsl(var(--accent))]" />
                    <Label htmlFor="req-pay" className="cursor-pointer">Requiere pre-pago (Stripe)</Label>
                  </div>
                  <Switch
                    id="req-pay"
                    checked={!!form.requires_payment}
                    onCheckedChange={(v) => setForm({ ...form, requires_payment: v })}
                    data-testid="service-requires-payment-switch"
                  />
                </div>
                {form.requires_payment && (
                  <div className="grid grid-cols-[1fr_120px] gap-3 items-center">
                    <Label className="text-xs text-slate-600">Depósito (%) del precio</Label>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={form.deposit_pct ?? 100}
                      onChange={(e) => setForm({ ...form, deposit_pct: e.target.value })}
                      data-testid="service-deposit-input"
                    />
                  </div>
                )}
                <div className="flex items-center justify-between pt-3 mt-3 border-t border-slate-200">
                  <div>
                    <Label htmlFor="iva-svc" className="cursor-pointer">Precio incluye IVA (16%)</Label>
                    <p className="text-[11px] text-slate-500 mt-0.5">Desactiva si emites la factura por separado o el servicio es exento.</p>
                  </div>
                  <Switch
                    id="iva-svc"
                    checked={form.iva_included !== false}
                    onCheckedChange={(v) => setForm({ ...form, iva_included: v })}
                    data-testid="service-iva-switch"
                  />
                </div>

                <div className="flex items-center justify-between pt-3 mt-3 border-t border-slate-200">
                  <div>
                    <Label htmlFor="overlap-svc" className="cursor-pointer">Permite empalme con otros servicios</Label>
                    <p className="text-[11px] text-slate-500 mt-0.5">Habilita agendar este servicio en paralelo con otros compatibles en una misma visita.</p>
                  </div>
                  <Switch
                    id="overlap-svc"
                    checked={!!form.overlap_enabled}
                    onCheckedChange={(v) => setForm({ ...form, overlap_enabled: v })}
                    data-testid="service-overlap-switch"
                  />
                </div>

                {form.overlap_enabled && (
                  <div>
                    <Label className="text-xs text-slate-600">Servicios compatibles</Label>
                    <div className="mt-2 border border-slate-200 rounded-md p-2 max-h-40 overflow-y-auto space-y-1" data-testid="service-compat-list">
                      {items.filter((s) => !editing || s.id !== editing.id).length === 0 && (
                        <p className="text-xs text-slate-400 p-2">Crea otro servicio primero para poder marcarlo como compatible.</p>
                      )}
                      {items.filter((s) => !editing || s.id !== editing.id).map((s) => {
                        const checked = (form.compatible_service_ids || []).includes(s.id);
                        return (
                          <label key={s.id} className="flex items-center gap-2 text-sm px-2 py-1 hover:bg-slate-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                const cur = new Set(form.compatible_service_ids || []);
                                if (e.target.checked) cur.add(s.id); else cur.delete(s.id);
                                setForm({ ...form, compatible_service_ids: Array.from(cur) });
                              }}
                              className="h-4 w-4 rounded border-slate-300"
                              data-testid={`service-compat-${s.id}`}
                            />
                            <span>{s.name}</span>
                            <span className="text-xs text-slate-400 ml-auto">{s.duration_min} min</span>
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">Solo aparecerán como opción en modo multi-servicio si ambos servicios están marcados entre sí.</p>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter className="shrink-0 border-t border-slate-200 px-6 py-4 bg-white">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} data-testid="service-save-btn">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="services-grid">
        {items.map((s) => (
          <Card key={s.id} className="border-slate-200 card-hover">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-2">
                <p className="overline">{s.category}</p>
                {s.requires_payment && (
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--accent))] flex items-center gap-1">
                    <CreditCard className="h-3 w-3" /> Depósito {s.deposit_pct || 100}%
                  </span>
                )}
              </div>
              <h3 className="font-display text-xl mt-2">{s.name}</h3>
              <p className="text-sm text-slate-600 mt-2 line-clamp-2">{s.description}</p>
              <div className="mt-5 flex items-center justify-between text-sm">
                <span className="text-slate-500">{s.duration_min} min</span>
                <div className="text-right">
                  <p className="font-medium">${s.price.toFixed(2)}</p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-0.5">
                    {s.iva_included !== false ? "IVA incluido" : "Sin IVA"}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => openEdit(s)} data-testid={`service-edit-${s.id}`}><Pencil className="h-3 w-3 mr-1" /> Editar</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(s.id)} data-testid={`service-delete-${s.id}`}><Trash2 className="h-3 w-3" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
